// Copyright (c) Docugami, Inc. All rights reserved.

import { redirectToCustomerPortal } from "billing/BillingService";
import { CoachMarkId } from "coachmarks/CoachMarkTypes";
import { UnassignedDocSetId } from "components/file_management/file_helpers";
import { DocSourceId, NavigationGroup, ReviewDocsId } from "components/myContentExplorer/ExplorerTypes";
import { RouteNavItem } from "components/shell/presentation/GlobalNavigationBar";
import { useAppSelector } from 'hooks/redux';
import { ReportViewContext } from "networking/reportsApi";
import { DocSetCollectionType, WorkPathType } from "networking/WorkPathApi";
import { DeveloperHubTarget } from "pages/DeveloperHubPage";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { selectIsActivePaidSubscription } from "store/AccountInfoHelpers";
import { getPageViewForReportViewMode } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { AppState } from "../store";
import { usePageLimits } from "./PageLimits";

export enum NavigationType {
    ReplaceCurrentPage,
    PushNewPage
}

export const WORK_PATH_PAGE = "workPath";
export const CHAT_PAGE = "chat";
export const QUERIES_PAGE = "queries";
export const QUERY_PAGE = "query";
export const SESSION_EXPIRED_PAGE = "session-expired";
export const DASHBOARD_PAGE = "dashboard";
export const ACCOUNT_PAGE = "account";
export const BILLING_PAGE = "tiers";
export const DEVELOPER_HUB_PAGE = "developer-playground";

export enum ReportViewMode {
    Document = 'Document',
    Table = 'Table',
    Abstract = 'Abstract',
    Chat = 'Chat'
}

export interface ReportQueryParams {
    documentId?: string;
    instanceId?: string;
    mode?: ReportViewMode;
    context?: ReportViewContext;
}

export interface INavigationFunctions {
    navToReportPage(navType: NavigationType, query?: ReportQueryParams): void;
    navToWorkPathPage(navType: NavigationType, workPathId?: string, documentId?: string, instanceId?: string): void;
    getWorkPathPageUrl(workPathId?: string, documentId?: string, instanceId?: string): string | undefined;
    navToNewWorkPathPage(navType: NavigationType, type: WorkPathType, docSetId?: string, collectionFilter?: DocSetCollectionType): void;
    navToChatPage(navType: NavigationType, docSetId?: string): void;

    navToHomePage(navType: NavigationType): void;
    navToDashboardProjectsPage(navType: NavigationType): void;
    navToDocSetPage(navType: NavigationType, docSetId?: string): void;
    navToDocSetsPage(): void;
    navToDocSetsWithExpanded(expandedDocSetId: string): void;
    navToDocSetDocumentsPage(docSetId: string): void;
    getDeepLinkToDocSetPage(docSetId: string): string | undefined;
    navToReviewDocs(navType: NavigationType, docSetId?: string): void;
    navToUnassignedFiles(navType: NavigationType): void;
    navToErrorFiles(navType: NavigationType): void;
    navToDocSources(navType: NavigationType): void;
    navToLocalDocs(navType: NavigationType): void;
    navToRemoteDocs(navType: NavigationType, remoteDocSourceId: DocSourceId): void;
    navToEmptyContentCoachmark(navType: NavigationType): void;
    navToOrganizationPage(): void;
    navToDashboardDiscoveryPage(): void;
    navToDiscoveryWithExpanded(discoveryId: string): void;
    navToDiscoveryDocumentsPage(discoverySetId: string): void;
    navToProjectsPage(navType: NavigationType, type: RouteNavItem.Understand | RouteNavItem.Create | RouteNavItem.Workflow): void;
    navToAccountPage(navType: NavigationType): void;
    navToDeveloperHub(target?: DeveloperHubTarget): void;
    navToSubscriptionUpgradePage(): void;
    goBack(): void;
    logout(): void;
}

export interface BuilderParam {
    pathPart: string;
    value: string;
}

// Helper for building a URL.
export class UrlBuilder {
    page: string;
    params: BuilderParam[] = [];
    query: BuilderParam[] = []
    orgId?: string;

    constructor(page: string) {
        this.page = page;
    }

    addOrg(orgId?: string): UrlBuilder {
        this.orgId = orgId;
        return this;
    }

    addParam(part: string, value?: string): UrlBuilder {
        if (!!value) {
            this.params.push({ pathPart: part, value: value });
        }

        return this;
    }

    addPart(part?: string): UrlBuilder {
        if (!!part) {
            this.params.push({ pathPart: part, value: '' });
        }

        return this;
    }

    addQuery(key: string, value?: string): UrlBuilder {
        if (!!value) {
            this.query.push({ pathPart: key, value: value });
        }

        return this;
    }

    getParamByPart(pathPart: string): BuilderParam | undefined {
        for (const param of this.params) {
            if (param.pathPart === pathPart) {
                return param;
            }
        }

        return undefined;
    }

    build(): string {
        let url = `/${this.page}`
        this.params.forEach(param => {
            url += `/${param.pathPart}`;
            if (!!param.value && param.value.length > 0) {
                url += `/${param.value}`;
            }
        });

        if (this.query.length > 0) {
            let isFirst = true;
            url += '?';
            this.query.forEach(param => {
                if (!isFirst) {
                    url += '&';
                }

                url += `${param.pathPart}=${encodeURIComponent(param.value)}`
                isFirst = false;
            })
        }

        if (!!this.orgId) {
            url += `#oid=${this.orgId}`;
        }

        return url;
    }
}

export const parseReportQueryParams = (search: string): ReportQueryParams => {
    const params = new URLSearchParams(search);
    const navContext: ReportQueryParams = {
        mode: ReportViewMode[params.get('mode') as keyof typeof ReportViewMode] ?? undefined,
        context: ReportViewContext[params.get('context') as keyof typeof ReportViewContext] ?? undefined,
        documentId: params.get('documentId') ?? undefined,
        instanceId: params.get('instanceId') ?? undefined
    }

    return navContext;
}

export function useNavigationService(): INavigationFunctions {
    const navigate = useNavigate();
    const location = useLocation();
    const orgId = useSelector((state: AppState) => state.organizations.currentOrganization?.id);

    const { pageUploadDisabled } = usePageLimits()
    const isActiveSubscriber = useSelector((state: AppState) => selectIsActivePaidSubscription(state));
    const currentWorkPath = useAppSelector(state => state.workPaths.currentWorkPath);

    function nav(url: string, navType: NavigationType) {
        if (navType === NavigationType.ReplaceCurrentPage) {
            navigate(url, { replace: true });
        } else {
            navigate(url);
        }
    }

    function logout() {
        nav("/logout", NavigationType.ReplaceCurrentPage)
    }

    function navToReportPage(navType: NavigationType, query: ReportQueryParams): void {
        if (!orgId || !currentWorkPath?.id) {
            return;
        }

        const existingQuery = parseReportQueryParams(location.search);
        const previousSearchParams = new URLSearchParams(window.location.href);
        const previousQuery = parseReportQueryParams(previousSearchParams.toString());

        if (existingQuery.documentId !== query.documentId) {
            // InstanceIds don't get persisted across document IDs
            delete existingQuery.instanceId;
        }

        const targetQuery: ReportQueryParams = {
            ...existingQuery,
            ...(query || {})
        };

        if (!!targetQuery.mode && targetQuery.mode !== previousQuery.mode) {
            Telemetry.trackPageView(getPageViewForReportViewMode(targetQuery.mode));

            if (targetQuery.mode !== ReportViewMode.Document) {
                // InstanceIds are only relevant to the document view
                delete targetQuery.instanceId;
            }
        }

        const urlBuilder = new UrlBuilder(WORK_PATH_PAGE)
            .addPart(currentWorkPath.id)
            .addQuery("context", targetQuery.context)
            .addQuery("mode", targetQuery.mode)
            .addQuery("documentId", targetQuery.documentId)
            .addQuery("instanceId", targetQuery.instanceId)
            .addOrg(orgId);

        nav(urlBuilder.build(), navType);
    }

    function navToHomePage(navType: NavigationType): void {
        if (!orgId) {
            return;
        }

        nav(`/home/#oid=${orgId}`, navType);
    }

    function navToDashboardProjectsPage(navType: NavigationType): void {
        navToContentPage(navType, NavigationGroup.Projects);
    }

    function navToDocSetPage(navType: NavigationType, docSetId?: string): void {
        navToContentPage(navType, docSetId ?? NavigationGroup.DocSets);
    }

    function navToDocSetsPage(): void {
        navToContentPage(NavigationType.ReplaceCurrentPage, NavigationGroup.DocSets);
    }

    function navToDocSetsWithExpanded(docSetId: string): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder(DASHBOARD_PAGE)
            .addPart(NavigationGroup.DocSets)
            .addPart(docSetId)
            .addOrg(orgId)
            .build();

        nav(url, NavigationType.ReplaceCurrentPage);
    }

    function navToDocSetDocumentsPage(docSetId: string): void {
        if (!orgId || !docSetId) {
            return;
        }

        if (docSetId === UnassignedDocSetId) {
            navToContentPage(NavigationType.PushNewPage, ReviewDocsId.Unassigned);
            return;
        }

        const url = new UrlBuilder(DASHBOARD_PAGE)
            .addPart(NavigationGroup.DocSets)
            .addPart(docSetId)
            .addPart('Documents')
            .addOrg(orgId)
            .build();

        nav(url, NavigationType.PushNewPage);
    }

    function getDeepLinkToDocSetPage(docSetId: string): string | undefined {
        if (!docSetId || !orgId) {
            return;
        }

        if (docSetId === UnassignedDocSetId) {
            return getDeepLinkToContentPage(ReviewDocsId.Unassigned);
        }

        return new UrlBuilder(DASHBOARD_PAGE)
            .addPart(NavigationGroup.DocSets)
            .addPart(docSetId)
            .addPart('Documents')
            .addOrg(orgId)
            .build();
    }

    function navToReviewDocs(navType: NavigationType): void {
        navToContentPage(navType, NavigationGroup.ReviewDocs);
    }

    function navToUnassignedFiles(navType: NavigationType): void {
        navToContentPage(navType, ReviewDocsId.Unassigned);
    }

    function navToErrorFiles(navType: NavigationType): void {
        navToContentPage(navType, ReviewDocsId.Error);
    }

    function navToDocSources(navType: NavigationType): void {
        navToContentPage(navType, NavigationGroup.DocSources);
    }

    function navToLocalDocs(navType: NavigationType): void {
        navToContentPage(navType, DocSourceId.Local);
    }

    function navToRemoteDocsPage(navType: NavigationType, remoteDocSourceId: DocSourceId): void {
        navToContentPage(navType, remoteDocSourceId);
    }

    function navToEmptyContentCoachmark(navType: NavigationType): void {
        navToContentPage(navType, CoachMarkId.EmptyDocSets);
    }

    function getDeepLinkToContentPage(targetId?: string, query?: [{ key: string, value: string }]): string | undefined {
        if (!orgId) {
            return;
        }

        const urlBuilder = new UrlBuilder(DASHBOARD_PAGE)
            .addPart(targetId)
            .addOrg(orgId);

        if (!!query && !!query.length) {
            query.forEach(queryItem => {
                urlBuilder.addQuery(queryItem.key, queryItem.value);
            })
        }

        return urlBuilder.build();
    }

    function navToContentPage(navType: NavigationType, targetId?: string, query?: [{ key: string, value: string }]): void {
        const url = getDeepLinkToContentPage(targetId, query);
        if (!url) {
            return;
        }

        nav(url, navType);
    }

    function navToOrganizationPage(): void {
        nav("/organization", NavigationType.ReplaceCurrentPage);
    }

    function getWorkPathPageUrl(workPathId?: string, documentId?: string, instanceId?: string): string | undefined {
        if (!orgId) {
            return;
        }

        workPathId = workPathId ?? currentWorkPath?.id;
        if (!workPathId) {
            return;
        }

        const existingQuery = parseReportQueryParams(location.search);
        const builder = new UrlBuilder(WORK_PATH_PAGE)
            .addPart(workPathId)
            .addQuery('mode', existingQuery.mode)
            .addQuery('context', existingQuery.context)
            .addQuery('documentId', !!documentId ? documentId : existingQuery.documentId)
            .addQuery('instanceId', !!instanceId ? instanceId : existingQuery.instanceId)
            .addOrg(orgId);

        return builder.build();
    }

    function navToWorkPathPage(navType: NavigationType, workPathId?: string, documentId?: string, instanceId?: string): void {
        const url = getWorkPathPageUrl(workPathId, documentId, instanceId);
        if (!url) {
            return;
        }

        nav(url, navType);
    }

    function navToProjectsPage(navType: NavigationType, type: RouteNavItem.Understand | RouteNavItem.Create | RouteNavItem.Workflow): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder('projects')
            .addPart(type)
            .addOrg(orgId)
            .build();

        nav(url, navType);
    }

    function navToDashboardDiscoveryPage(): void {
        navToContentPage(NavigationType.ReplaceCurrentPage, NavigationGroup.Discovery);
    }

    function navToDiscoveryWithExpanded(discoveryId: string): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder(DASHBOARD_PAGE)
            .addPart(NavigationGroup.Discovery)
            .addQuery('discoveryId', discoveryId)
            .addOrg(orgId)
            .build();

        nav(url, NavigationType.ReplaceCurrentPage);
    }

    function navToDiscoveryDocumentsPage(discoverySetId: string): void {
        if (!orgId || !discoverySetId) {
            return;
        }

        const url = new UrlBuilder(DASHBOARD_PAGE)
            .addPart(NavigationGroup.Discovery)
            .addPart(discoverySetId)
            .addPart('Documents')
            .addOrg(orgId)
            .build();

        nav(url, NavigationType.PushNewPage);
    }

    function navToNewWorkPathPage(navType: NavigationType, type: WorkPathType, docSetId?: string, collectionFilter?: DocSetCollectionType): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder(WORK_PATH_PAGE)
            .addParam("new", type.toString())
            .addOrg(orgId)
            .addQuery('docSetId', docSetId)
            .addQuery('docSetsFilter', collectionFilter)
            .build();

        nav(url, navType);
    }

    function navToChatPage(navType: NavigationType, docSetId?: string): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder(CHAT_PAGE)
            .addQuery('docSetId', docSetId)
            .addOrg(orgId)
            .build();

        nav(url, navType);
    }

    function navToAccountPage(navType: NavigationType): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder(ACCOUNT_PAGE)
            .addOrg(orgId)
            .build();

        nav(url, navType);
    }

    function navToSubscriptionUpgradePage(): void {
        if (!orgId) {
            return;
        }

        if (isActiveSubscriber) {
            redirectToCustomerPortal(orgId);
        } else {
            navToBillingPage();
        }
    }

    function navToBillingPage(): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder(BILLING_PAGE)
            .addOrg(orgId)
            .build();

        nav(url, NavigationType.PushNewPage);
    }

    function navToDeveloperHub(target?: DeveloperHubTarget): void {
        if (!orgId) {
            return;
        }

        const url = new UrlBuilder(DEVELOPER_HUB_PAGE).addOrg(orgId).addPart(target).build();
        nav(url, NavigationType.PushNewPage);
    }

    function goBack(): void {
        navigate(-1);
    }

    return useMemo(() => {
        return {
            navToReportPage: navToReportPage,
            navToHomePage: navToHomePage,
            navToDashboardProjectsPage: navToDashboardProjectsPage,
            navToDocSetPage: navToDocSetPage,
            navToDocSetsPage: navToDocSetsPage,
            navToDocSetsWithExpanded: navToDocSetsWithExpanded,
            navToDocSetDocumentsPage: navToDocSetDocumentsPage,
            getDeepLinkToDocSetPage: getDeepLinkToDocSetPage,
            navToReviewDocs: navToReviewDocs,
            navToUnassignedFiles: navToUnassignedFiles,
            navToErrorFiles: navToErrorFiles,
            navToDocSources: navToDocSources,
            navToLocalDocs: navToLocalDocs,
            navToRemoteDocs: navToRemoteDocsPage,
            navToEmptyContentCoachmark: navToEmptyContentCoachmark,
            navToOrganizationPage: navToOrganizationPage,
            navToWorkPathPage: navToWorkPathPage,
            getWorkPathPageUrl: getWorkPathPageUrl,
            navToNewWorkPathPage: navToNewWorkPathPage,
            navToChatPage: navToChatPage,
            navToDashboardDiscoveryPage: navToDashboardDiscoveryPage,
            navToDiscoveryWithExpanded: navToDiscoveryWithExpanded,
            navToDiscoveryDocumentsPage: navToDiscoveryDocumentsPage,
            navToProjectsPage: navToProjectsPage,
            navToAccountPage: navToAccountPage,
            navToSubscriptionUpgradePage: navToSubscriptionUpgradePage,
            navToDeveloperHub: navToDeveloperHub,
            goBack: goBack,
            logout: logout
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [navigate, orgId, pageUploadDisabled, isActiveSubscriber, currentWorkPath?.id, location]);
}
